import {
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Typography,
} from '@material-ui/core';
import { DelayedLinearProgress, ResponsiveIconButton } from 'components';
import { useLocalStorageSettings } from 'hooks';
import { PendingItem } from 'interfaces';
import * as React from 'react';
import { OrderService, SettingsService } from 'services';
import { collapseCombos } from '../../collapseCombos';
import { FirebaseService } from 'services/FirebaseService';
import { PendingOrderCard } from './components';
import RefreshIcon from '@material-ui/icons/Refresh';

export const PendingItemView: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [starredOnly, setStarredOnly] = React.useState(false);
  const [items, setItems] = React.useState<
    { orderId: number; starred: boolean; items: PendingItem[] }[]
  >([]);
  const [publishNowEnabled, setPublishNowEnabled] = React.useState(false);
  const [savedStarred, setSavedStarred] = useLocalStorageSettings<number[]>(
    'starred_orders',
    [],
  );

  // Fetch the Publish Now kill-switch setting once on mount. Default to
  // false (button hidden) if the fetch fails — safer than showing a
  // feature with unknown state.
  React.useEffect(() => {
    SettingsService.getSettings()
      .then((settings) => {
        const row = settings.find(
          (s: { key: string; value: string }) => s.key === 'publish_now_enabled',
        );
        setPublishNowEnabled(!!row && String(row.value) === '1');
      })
      .catch(() => setPublishNowEnabled(false));
  }, []);

  const loadOrderItems = React.useCallback(
    function loadOrderItems() {
      setLoading(true);
      Promise.all([OrderService.getOrderItemsForPrepartion()])
        .then(([orderResponse]) => {
          const orders = orderResponse.reduce<
            { orderId: number; starred: boolean; items: PendingItem[] }[]
          >((prev, curr) => {
            let orderIndex = prev.findIndex((p) => p.orderId === curr.orderId);

            const splitItems =
              curr.quantity > 1
                ? Array.from(Array(curr.quantity).keys()).map((c) => ({
                    ...curr,
                    quantity: 1,
                  }))
                : [curr];

            if (orderIndex === -1) {
              return [
                ...prev,
                {
                  orderId: curr.orderId,
                  starred: savedStarred.includes(curr.orderId),
                  items: [...splitItems],
                },
              ];
            } else {
              prev[orderIndex].items.push(...splitItems);
            }

            return prev;
          }, []);

          // Shared with the lids screen so both always render combos the
          // same way (see collapseCombos).
          const collapsed = orders.map((order) => ({
            ...order,
            items: collapseCombos(order.items),
          }));

          setItems(collapsed);
        })
        .finally(() => setLoading(false));
    },
    [savedStarred],
  );

  React.useEffect(() => {
    loadOrderItems();
  }, [loadOrderItems]);

  // Live refresh: re-fetch when Firebase signals an /orders mutation.
  // Debounced so a burst of events results in a single API roundtrip.
  // The manual Refresh button stays as a fallback if Firebase drifts.
  React.useEffect(() => {
    const ref = FirebaseService.getDatabase().ref('orders');
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handler = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        loadOrderItems();
      }, 1500);
    };
    ref.on('value', handler);
    return () => {
      ref.off('value', handler);
      if (timer) clearTimeout(timer);
    };
  }, [loadOrderItems]);

  return (
    <React.Fragment>
      <Paper
        style={{
          padding: 16,
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <ResponsiveIconButton
          onClick={(e) => {
            loadOrderItems();
          }}
          color="primary"
          icon={RefreshIcon}
        >
          Refresh Orders
        </ResponsiveIconButton>

        <Typography variant="subtitle2" color="inherit">
          Number of orders to pack: {items.length}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              value="starredOnly"
              checked={starredOnly}
              onChange={(evt, checked) => setStarredOnly(checked)}
            />
          }
          label="Show Starred Only"
        />
      </Paper>

      {loading && <DelayedLinearProgress />}
      <Grid container>
        {items
          .filter((i) => !starredOnly || i.starred === starredOnly)
          .map((order, index) => (
            <PendingOrderCard
              key={order.orderId}
              order={order}
              publishNowEnabled={publishNowEnabled}
              onPackOrder={(orderId: number) => {
                let newVal = items.slice();

                newVal = newVal.filter((v) => v.orderId !== orderId);

                setItems(newVal);
              }}
              onPublishNow={() => {
                // Refetch so the publishable flag flips to false on
                // affected orders; Firebase listener will also fire
                // but this is the immediate UI update.
                loadOrderItems();
              }}
              toggleStarred={(orderId: number) => {
                const newVal = items.slice();
                const orderIndex = newVal.findIndex(
                  (v) => v.orderId === orderId,
                );

                if (orderIndex !== -1) {
                  newVal[orderIndex].starred = !newVal[orderIndex].starred;
                }

                setItems(newVal);

                setSavedStarred(
                  newVal.filter((v) => v.starred).map((v) => v.orderId),
                );
              }}
            />
          ))}
      </Grid>
    </React.Fragment>
  );
};
